import Backbone from '@polleverywhere/backbone'
import './style.scss'
import Template from './template.hamlc'
import BEM from '@polleverywhere/toolkit-lib/lib/mixins/_bem'
import _ from 'underscore'
import DOMPurify from 'dompurify'

/**
 * Advisory messages are used to display a status that may require you to acknowledge and perform
 * some action to proceed. It is not meant to be displayed as an overlay and should be rendered either
 * full screen or inline with contextual content. Common use cases are empty lists such as empty trash page,
 * empty group page, 404/500 errors, waiting status during a bulk or long running operation.
 *
 * @event resolve Resolution button clicked
 * @event abort Abort button clicked
 *
 * @public
 */
export default class AdvisoryMessage extends Backbone.View {
  get className () { return 'component-advisory-message' }

  // Accessibility attribute that announce presence to screen readers
  get attributes () {
    return {
      'aria-live': 'polite',
      role: 'region'
    }
  }

  get events () {
    return {
      'click button[value="resolve"]': () => {
        this.trigger('resolve')
      },
      'click button[value="abort"]': () => {
        this.trigger('abort')
      }
    }
  }

  /*
   * Initialize
   *
   * @param {Object} options
   * @param {String|HTMLElement} options.icon Icon name from assets or Element that will be injected in. (Default: 'info')
   * @param {String|HTMLElement} options.iconName Deprecated option, replaced by 'icon'
   * @param {Boolean} options.spinIcon Spin the icon (Default: false)
   * @param {String} options.subject Subject line
   * @param {String} options.message Longer HTML text for context
   * @param {String} options.resolutionLabel Button CTA
   * @param {String} options.abortLabel Button CTA
   * @param {String} options.alignment Text alignment 'left' or 'center' (Default: 'left')
   */
  initialize (options = {}) {
    _.extend(this, BEM(this))

    // Handle legacy iconName option
    // TODO: remove at next major version
    if (!options.icon && options.iconName) {
      options.icon = options.iconName
    }

    this.options = _.defaults(options, {
      icon: 'info',
      spinIcon: false,
      subject: '',
      message: '',
      resolutionLabel: false,
      secondaryResolutionLabel: false,
      alignment: 'left'
    })

    if (this.options.alignment !== 'left') {
      this.$_toggleModifier(null, this.className, this.options.alignment, true)
    }

    if (this.options.spinIcon) {
      this.$_toggleModifier(null, this.className, 'spin', true)
    }
  }

  render () {
    this.$el.html(Template())

    if (_.isString(this.options.icon)) {
      this.$_element('icon').attr('data-glyph', this.options.icon)
    } else {
      this.$_element('icon').append(this.options.icon)
    }

    this.$_element('subject').text(this.options.subject)
    this.$_element('message').html(DOMPurify.sanitize(this.options.message, { SAFE_FOR_JQUERY: true }))

    if (this.options.resolutionLabel) {
      this.$_element('button[value=resolve]').text(this.options.resolutionLabel)
    }

    if (this.options.abortLabel) {
      this.$_element('button[value=abort]').text(this.options.abortLabel)
    } else {
      this.$_element('button[value=abort]').remove()
    }

    return this
  }
}
